import React, { useState, useRef, useCallback } from "react";
import ResumenPorEmpresa from "../../components/financiero/ResumenPorEmpresa";
import { toast } from "react-toastify";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { useSession } from "@/context/SessionProvider";
import BalanceAdjustments from "@/components/financiero/balances/BalanceAdjustments";
import TarifasPerfilCard from "@/components/financiero/balances/TarifasPerfilCard";

/* ====== Helpers numéricos con caret ====== */
const sanitizeNumeric = (s, maxDecimals = 2) => {
    if (!s) return "";
    s = String(s).replace(/[^0-9.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    if (firstDot !== -1) {
        const i = s.slice(0, firstDot);
        const d = s.slice(firstDot + 1, firstDot + 1 + maxDecimals);
        s = `${i}.${d}`;
    }
    if (!s.includes(".")) s = s.replace(/^0+(?=\d)/, "");
    return s;
};
const formatCommas = (raw) => {
    if (raw === "" || raw == null) return "";
    const [i, d] = String(raw).split(".");
    const withCommas = (i || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return d != null && d !== "" ? `${withCommas}.${d}` : withCommas;
};
const FormattedNumberInput = ({ valueRaw, onChangeRaw, decimals = 2, className = "", placeholder = "", ...rest }) => {
    const ref = useRef(null);
    const display = formatCommas(valueRaw);
    const caretFormattedToRaw = (formatted, caret) => {
        let r = 0;
        for (let i = 0; i < caret; i++) if (formatted[i] !== ",") r++;
        return r;
    };
    const caretRawToFormatted = (formatted, rawIndex) => {
        if (rawIndex <= 0) return 0;
        let count = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (formatted[i] !== ",") count++;
            if (count === rawIndex) return i + 1;
        }
        return formatted.length;
    };
    const handleChange = (e) => {
        const typed = e.target.value;
        const caret = e.target.selectionStart ?? typed.length;
        const rawCaret = caretFormattedToRaw(typed, caret);
        const raw = sanitizeNumeric(typed, decimals);
        const formatted = formatCommas(raw);
        const newCaret = caretRawToFormatted(formatted, rawCaret);
        onChangeRaw(raw);
        requestAnimationFrame(() => {
            if (ref.current) ref.current.setSelectionRange(newCaret, newCaret);
        });
    };
    return (
        <input
            ref={ref}
            type="text"
            inputMode="decimal"
            className={className}
            value={display}
            onChange={handleChange}
            placeholder={placeholder}
            {...rest}
        />
    );
};

export default function Financiero() {
    const { user } = useSession();
    const perfilId = user?.perfil_id ?? 0;

    const [mostrarFormulario, setMostrarFormulario] = useState(false);

    /* ===== Estado de filas de tarifas (UI) ===== */
    const [tarifasRows, setTarifasRows] = useState([
        { tipo_llamada: "", rango_min_inicio: "", rango_min_fin: "", tarifa: "", fecha_inicio: new Date().toISOString().slice(0, 16) },
    ]);

    // Estado para sincronizar con TarifasPerfilCard
    const [modoPerfil, setModoPerfil] = useState("nuevo");
    const [perfilSeleccionadoId, setPerfilSeleccionadoId] = useState(null);

    // Callbacks para sincronizar estado con el hijo
    const handleModoChange = useCallback((modo) => {
        setModoPerfil(modo);
    }, []);

    const handlePerfilSeleccionadoChange = useCallback((perfilId) => {
        setPerfilSeleccionadoId(perfilId);
    }, []);

    // Valida mínimo para habilitar el submit final
    const tarifasValidas =
        tarifasRows.length > 0 &&
        tarifasRows.every((r) => {
            const ini = Number(r.rango_min_inicio);
            const fin = Number(r.rango_min_fin);
            const tv = Number(r.tarifa);
            return (
                r.tipo_llamada &&
                r.fecha_inicio &&
                r.rango_min_inicio !== "" &&
                r.rango_min_fin !== "" &&
                r.tarifa !== "" &&
                Number.isFinite(ini) &&
                Number.isFinite(fin) &&
                Number.isFinite(tv) &&
                ini >= 0 &&
                fin >= ini &&
                tv >= 0
            );
        });

    /* ===== Form alta básica ===== */
    const [empresaNombre, setEmpresaNombre] = useState("");
    const [clienteNombre, setClienteNombre] = useState("");
    const [tipoCuenta, setTipoCuenta] = useState("postpago");
    const [limiteCredito, setLimiteCredito] = useState("");
    const [anticipo, setAnticipo] = useState("");

    /* ===== Resoluciones / datos remotos ===== */
    const [empresaId, setEmpresaId] = useState(null);
    const [clientesEmpresa, setClientesEmpresa] = useState([]);
    const [loadingEmpresa, setLoadingEmpresa] = useState(false);
    const [loadingClientes, setLoadingClientes] = useState(false);

    // Validación que depende del modo
    const canSubmitTodo =
        empresaNombre.trim().length > 0 &&
        clienteNombre.trim().length > 0 &&
        (modoPerfil === "existente"
            ? perfilSeleccionadoId !== null
            : tarifasValidas
        );

    const cargarClientesDeEmpresa = async (eid) => {
        try {
            setLoadingClientes(true);
            const url = API_ROUTES.clientesList || API_ROUTES.listadoClientes;
            const { data } = await axios.get(url, { withCredentials: true });
            const arr = Array.isArray(data) ? data : [];
            const porEmpresa = arr.filter((c) => String(c.empresa_id) === String(eid));
            setClientesEmpresa(porEmpresa);
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "No se pudieron cargar clientes");
            setClientesEmpresa([]);
        } finally {
            setLoadingClientes(false);
        }
    };

    const crearTodoConTarifas = async (payload) => {
        try {
            if (!empresaNombre.trim()) throw new Error("Escribe el nombre de la empresa");
            if (!clienteNombre.trim()) throw new Error("Escribe el nombre del cliente");

            setLoadingEmpresa(true);

            // 1) Empresa
            const empRes = await axios.post(
                API_ROUTES.ensureEmpresa,
                { nombre: empresaNombre.trim() },
                { withCredentials: true }
            );
            const eid = empRes?.data?.empresa_id ?? empRes?.data?.id ?? empRes?.data?.data?.empresa_id;
            if (!eid) throw new Error("No se pudo obtener el ID de empresa");
            setEmpresaId(Number(eid));

            // 2) Cliente
            const cliRes = await axios.post(
                API_ROUTES.ensureCliente,
                { empresa_id: Number(eid), nombre: clienteNombre.trim(), tipo_cuenta: tipoCuenta },
                { withCredentials: true }
            );
            const cid = cliRes?.data?.cliente_id ?? cliRes?.data?.id ?? cliRes?.data?.data?.cliente_id;
            if (!cid) throw new Error("No se pudo obtener el ID del cliente");

            // 3) Límite / Depósito (solo postpago)
            if (tipoCuenta === "postpago") {
                const credito = Number(limiteCredito || 0);
                const deposito = Number(anticipo || 0);
                if (credito > 0) {
                    await axios.post(
                        API_ROUTES.setLimiteEmpresa,
                        { empresa_id: Number(eid), credito_base: credito, comentario: "Alta: límite inicial" },
                        { withCredentials: true }
                    );
                }
                if (deposito > 0) {
                    await axios.post(
                        API_ROUTES.depositoEmpresa,
                        {
                            empresa_id: Number(eid),
                            naturaleza: "abono",
                            monto: deposito,
                            comentario: "Alta: depósito inicial",
                            fecha_registro_pago: `${new Date().toISOString().slice(0, 10)} 00:00:00`,
                        },
                        { withCredentials: true }
                    );
                }
            }

            // 4) Manejar perfil según modo
            let perfilIdFinal;

            if (payload.modo === "existente") {
                // Usar perfil existente
                perfilIdFinal = payload.perfil_id;
            } else {
                // Crear nuevo perfil con tarifas
                if (!Array.isArray(payload.items) || payload.items.length === 0) {
                    throw new Error("Agrega al menos una tarifa");
                }

                const payloadPerfil = {
                    nombre: payload.nombre || null,
                    descripcion: payload.descripcion || null,
                    items: payload.items.map((r) => ({
                        tipo_llamada: r.tipo_llamada,
                        rango_min_inicio: Number(r.rango_min_inicio),
                        rango_min_fin: Number(r.rango_min_fin),
                        tarifa: Number(r.tarifa),
                        fecha_inicio: String(r.fecha_inicio || "").replace("T", " "),
                    }))
                };

                const perfilRes = await axios.post(
                    API_ROUTES.listadoCrearPerfilTarifasNuevo,
                    payloadPerfil,
                    { withCredentials: true }
                );
                perfilIdFinal = perfilRes?.data?.perfil_id ?? perfilRes?.data?.data?.perfil_id;
                if (!perfilIdFinal) throw new Error("No se devolvió perfil_id");
            }

            // 5) Asignar perfil al cliente
            await axios.post(
                API_ROUTES.listadoAsignarPerfil,
                { cliente_id: Number(cid), perfil_id: Number(perfilIdFinal) },
                { withCredentials: true }
            );

            toast.success(
                payload.modo === "existente"
                    ? `Cliente creado y perfil #${perfilIdFinal} asignado`
                    : `Empresa, cliente y perfil #${perfilIdFinal} creados y asignados`
            );

            await cargarClientesDeEmpresa(eid);

            // Reset del formulario
            setMostrarFormulario(false);
            setEmpresaNombre("");
            setClienteNombre("");
            setTipoCuenta("postpago");
            setLimiteCredito("");
            setAnticipo("");
            setTarifasRows([{
                tipo_llamada: "",
                rango_min_inicio: "",
                rango_min_fin: "",
                tarifa: "",
                fecha_inicio: new Date().toISOString().slice(0, 16)
            }]);
            setModoPerfil("nuevo");
            setPerfilSeleccionadoId(null);

        } catch (e) {
            console.error('Error en crearTodoConTarifas:', e);
            toast.error(e?.response?.data?.message || e?.message || "No se pudo crear el conjunto");
        } finally {
            setLoadingEmpresa(false);
        }
    };

    /* ===================== JSX ===================== */
    return (
        <div className="px-6 py-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Panel de Control</h2>

                {[1, 5].includes(perfilId) && !mostrarFormulario && (
                    <button
                        onClick={() => setMostrarFormulario(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Dar de alta
                    </button>
                )}

                {mostrarFormulario && (
                    <button
                        onClick={() => setMostrarFormulario(false)}
                        className="text-blue-600 hover:underline"
                    >
                        Regresar
                    </button>
                )}
            </div>

            {!mostrarFormulario ? (
                <>
                    <div className="flex gap-8 border-b mb-6">
                        <span className="pb-2 border-b-2 border-blue-600 text-blue-600 font-semibold">
                            {[1, 5].includes(perfilId) ? "Consultar clientes" : "Mis datos"}
                        </span>
                    </div>
                    <ResumenPorEmpresa modo={[1, 5].includes(perfilId) ? "admin" : "empresa"} />
                </>
            ) : (
                <div className="w-full max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* IZQUIERDA: Alta */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="border rounded-xl shadow-sm p-6 bg-white space-y-4">
                                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                                    Información básica del cliente
                                </h3>

                                <div>
                                    <label className="text-sm font-medium mb-1 block">Empresa *</label>
                                    <input
                                        type="text"
                                        value={empresaNombre}
                                        onChange={(e) => setEmpresaNombre(e.target.value)}
                                        className="border rounded px-3 py-2 w-full"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-1 block">Cliente *</label>
                                    <input
                                        type="text"
                                        value={clienteNombre}
                                        onChange={(e) => setClienteNombre(e.target.value)}
                                        className="border rounded px-3 py-2 w-full"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Tipo de cuenta *</label>
                                    <div className="flex gap-6">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="tipoCuenta"
                                                value="postpago"
                                                checked={tipoCuenta === "postpago"}
                                                onChange={() => setTipoCuenta("postpago")}
                                            />
                                            Postpago
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="tipoCuenta"
                                                value="prepago"
                                                checked={tipoCuenta === "prepago"}
                                                onChange={() => setTipoCuenta("prepago")}
                                            />
                                            Prepago
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {tipoCuenta === "postpago" && (
                                <div className="border rounded-xl shadow-sm p-6 bg-white">
                                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
                                        Configuración financiera (inicial)
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Límite de crédito (MXN)</label>
                                            <FormattedNumberInput
                                                valueRaw={limiteCredito}
                                                onChangeRaw={setLimiteCredito}
                                                decimals={2}
                                                className="border rounded px-3 py-2 w-full"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Depósito (MXN)</label>
                                            <FormattedNumberInput
                                                valueRaw={anticipo}
                                                onChangeRaw={setAnticipo}
                                                decimals={2}
                                                className="border rounded px-3 py-2 w-full"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Ficha de Tarifas con botón final */}
                            <div className="border rounded-xl shadow-sm p-6 bg-white">
                                <TarifasPerfilCard
                                    rows={tarifasRows}
                                    setRows={setTarifasRows}
                                    onSubmit={crearTodoConTarifas}
                                    submitting={loadingEmpresa}
                                    canSubmit={canSubmitTodo}
                                    onModoChange={handleModoChange}
                                    onPerfilSeleccionadoChange={handlePerfilSeleccionadoChange}
                                />
                            </div>
                        </div>

                        {/* DERECHA: Ajustes */}
                        <div className="lg:col-span-2">
                            <div className="border rounded-xl shadow-sm p-6 bg-white">
                                <h3 className="text-lg font-semibold text-gray-800 border-b pb-4 mb-6">
                                    Ajustes de Balance
                                </h3>

                                {!empresaId ? (
                                    <div className="text-sm text-slate-500">
                                        Crea/asegura la empresa y el cliente para habilitar los ajustes.
                                    </div>
                                ) : (
                                    <>
                                        {loadingClientes && <div className="text-sm text-slate-500 mb-3">Cargando clientes…</div>}
                                        <BalanceAdjustments
                                            empresaId={Number(empresaId)}
                                            clientes={clientesEmpresa}
                                            onDone={() => {
                                                toast.success("Ajustes aplicados");
                                                window.dispatchEvent(
                                                    new CustomEvent("finanzas-changed", { detail: { empresa_id: Number(empresaId) } })
                                                );
                                            }}
                                            onClose={() => { }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}